import { Avatar, Card, Tooltip, Typography } from '@material-ui/core';
import {
  AccountBalance,
  Delete,
  PictureAsPdf,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { useSignMedia } from 'shared/data/MediaServiceAPI';
import { getFilenameFromURL } from 'shared/helpers/CommonHelpers';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { ACHPaymentInfoDTO } from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import { LinkButton } from 'shared/routing/Links';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import styled from 'styled-components';

const BankAvatar = styled(Avatar)`
  width: 56px;
  height: 56px;
  background: ${ColorDynamic.Silver200};
  border: 1px solid ${ColorDynamic.Silver400};
`;

interface ACHBankDetailsActionsProps {
  hoursUntilUpdateAllowed?: number | null;
  onDelete: () => void;
  isDeleting?: boolean;
}

function ACHBankDetailsActions({
  hoursUntilUpdateAllowed,
  onDelete,
  isDeleting,
}: ACHBankDetailsActionsProps) {
  const isUpdateBlocked = !!(
    hoursUntilUpdateAllowed && hoursUntilUpdateAllowed > 0
  );

  const tooltipTitle = isUpdateBlocked
    ? `You have changed your email/phone in the last 24 hours. You can't change ACH bank details for ${hoursUntilUpdateAllowed} ${formatPlural(hoursUntilUpdateAllowed, 'hour', 'hours')}.`
    : '';

  return (
    <Columns space="small" align="center">
      <Column>
        <PermissionAction name="ach_payment_info" permission="can_edit">
          <Tooltip
            title={tooltipTitle}
            disableHoverListener={!isUpdateBlocked}
            enterTouchDelay={0}
            leaveTouchDelay={5000}
            arrow={true}
          >
            <Box>
              <LinkButton
                variant="neutral"
                to="change-bank-details"
                fullWidth={true}
                disabled={isUpdateBlocked}
              >
                Change ACH Bank Details
              </LinkButton>
            </Box>
          </Tooltip>
        </PermissionAction>
      </Column>
      <Column width="content">
        <PermissionButton
          name="ach_payment_info"
          permission="can_edit"
          variant="neutral"
          aria-label="Delete ACH Bank Details"
          pending={isDeleting}
          onClick={onDelete}
        >
          <Delete fontSize="small" color="action" />
        </PermissionButton>
      </Column>
    </Columns>
  );
}

function maskWithDots(value: string, visibleCount: number, dotsCount = 4) {
  if (!value) return '—';
  return `${'•'.repeat(dotsCount)}${value.slice(-visibleCount)}`;
}

interface ACHBankDetailsViewProps {
  paymentInfo: ACHPaymentInfoDTO;
  onDelete: () => void;
  isDeleting?: boolean;
}

export function ACHBankDetailsView({
  paymentInfo,
  onDelete,
  isDeleting,
}: ACHBankDetailsViewProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const isMobile = useResponsiveValue(true, false);
  const { data: signedMedia, isLoading: isLoadingSignedMedia } = useSignMedia(
    paymentInfo.voided_check_url,
  );

  return (
    <Card>
      <Box padding="small">
        <Stack space="medium">
          <Columns align="center" space="small">
            <Column>
              <Typography variant="h3">ACH Bank Details</Typography>
            </Column>
            {!isMobile && (
              <Column width="content">
                <ACHBankDetailsActions
                  hoursUntilUpdateAllowed={
                    paymentInfo.hours_until_update_allowed
                  }
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                />
              </Column>
            )}
          </Columns>

          <Typography color="textSecondary">
            ACH bank details helps Shippers to process payments. Shippers can
            see this information.
          </Typography>

          <Columns
            align={['bottom', 'center']}
            collapseBelow="tablet"
            space={['none', 'small']}
          >
            <Column>
              <Inline verticalAlign="center" space="small">
                <BankAvatar>
                  <AccountBalance color="action" />
                </BankAvatar>
                <Stack space="none">
                  <Typography variant="h3">
                    {paymentInfo.bank_name}{' '}
                    {isRevealed
                      ? paymentInfo.bank_account_number || '—'
                      : maskWithDots(paymentInfo.bank_account_number, 4)}
                  </Typography>
                  <Typography color="textSecondary">
                    Routing Number{' '}
                    {isRevealed
                      ? paymentInfo.bank_routing_number || '—'
                      : maskWithDots(paymentInfo.bank_routing_number, 5, 5)}
                  </Typography>
                </Stack>
              </Inline>
            </Column>
            <Column width="content">
              <Button
                variant="text"
                startIcon={isRevealed ? <VisibilityOff /> : <Visibility />}
                onClick={() => {
                  setIsRevealed((prev) => !prev);
                }}
              >
                {isRevealed ? 'Hide' : 'Show'}
              </Button>
            </Column>
          </Columns>

          <Stack space="xsmall">
            <Typography variant="h4">Voided Check</Typography>
            {isLoadingSignedMedia ? (
              <Skeleton height="16px" />
            ) : signedMedia?.signed_url ? (
              <Inline verticalAlign="center">
                <PictureAsPdf fontSize="small" color="action" />
                <WebViewLink
                  href={signedMedia.signed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getFilenameFromURL(paymentInfo.voided_check_url)}
                </WebViewLink>
              </Inline>
            ) : (
              <Typography color="textSecondary">No data</Typography>
            )}
          </Stack>

          {isMobile && (
            <ACHBankDetailsActions
              hoursUntilUpdateAllowed={paymentInfo.hours_until_update_allowed}
              onDelete={onDelete}
              isDeleting={isDeleting}
            />
          )}
        </Stack>
      </Box>
    </Card>
  );
}
